import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Star, Mail, Users, Calendar, Youtube, MapPin } from "lucide-react";
import { formatListenerCount } from "@/utils/format-utils";
import { buildAppleUrl, REGION_NAMES, REGION_FLAGS } from "@/utils/region-utils";
import { ImageWithFallback } from "@/components/common/ImageWithFallback";
import { Podcast } from "@/types/podcast";
import { stripHtmlTags } from "@/utils/html-utils";
import { Button } from "@/components/ui/button";
import dayjs from "dayjs";
import { MatchedEpisodes } from "./MatchedEpisodes";
import { ListPlus } from "lucide-react";

interface PodcastTableProps {
  podcasts: Podcast[];
  onClick: (podcast: Podcast) => void;
  onAddToCampaign: (podcast: Podcast) => void;
}

export function PodcastTable({ podcasts, onClick, onAddToCampaign }: PodcastTableProps) {
  return (
    <div className="rounded-md border bg-white overflow-hidden">
      <Table className="table-fixed">
        <TableHeader className="bg-slate-50">
          <TableRow>
            <TableHead className="w-auto">Podcast</TableHead>
            <TableHead className="w-[300px]">Audience Stats</TableHead>
            <TableHead className="w-[80px] text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {podcasts.map((podcast) => (
            <TableRow 
                key={podcast.id} 
                className="cursor-pointer hover:bg-muted/50 group" 
                onClick={() => onClick(podcast)}
            >
              <TableCell className="align-top py-3 whitespace-normal overflow-hidden">
                <div className="flex gap-4 items-start min-w-0">
                   <div className="size-16 rounded-lg bg-muted flex-shrink-0 overflow-hidden border border-border/50">
                    <ImageWithFallback
                        src={podcast.thumbnailUrl || podcast.imageUrl}
                        alt={podcast.title}
                        className="size-full object-cover"
                    />
                    </div>
                    <div className="space-y-1.5 min-w-0 overflow-hidden">
                        <div className="flex items-center gap-2">
                             <span className="font-semibold text-sm line-clamp-1 text-foreground group-hover:text-primary transition-colors">
                                {podcast.title}
                            </span>
                             {podcast.language && (
                                <Badge variant="secondary" className="text-[10px] px-1.5 h-4 bg-slate-100 text-slate-600 hover:bg-slate-200 border-0">
                                    {podcast.language.toUpperCase()}
                                </Badge>
                            )}
                        </div>
                        
                        <div className="text-xs text-muted-foreground line-clamp-1">by {podcast.author}</div>
                        
                        <p className="text-xs text-slate-500 hidden md:block">
                            {stripHtmlTags(podcast.description).length > 150 
                                ? `${stripHtmlTags(podcast.description).substring(0, 150)}...` 
                                : stripHtmlTags(podcast.description)}
                        </p>

                        {/* Matched Episodes Section */}
                        <MatchedEpisodes episodes={podcast.matchedEpisodes} />
                    </div>
                </div>
              </TableCell>
              <TableCell className="align-top py-3">
                 <div className="flex flex-wrap gap-2 items-start content-start">
                    {/* Rating */}
                    {podcast.appleRating ? (
                        (podcast.externalLinks?.applePodcasts || podcast.itunesId) ? (
                        <a 
                            href={podcast.externalLinks?.applePodcasts || (podcast.itunesId ? buildAppleUrl(podcast.itunesId, podcast.primaryMarket) : '#')}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1 bg-amber-50 border border-amber-200 px-1.5 py-0 rounded text-amber-700 h-5 hover:bg-amber-100 transition-colors"
                            title={`View on Apple Podcasts${podcast.primaryMarket ? ` (${REGION_NAMES[podcast.primaryMarket] || podcast.primaryMarket.toUpperCase()})` : ''}`}
                        >
                            <Star className="size-3 text-amber-500 fill-amber-500" />
                            <span className="font-semibold text-[10px] leading-none">{podcast.appleRating.toFixed(1)}</span>
                            <span className="text-[10px] text-amber-600/80 ml-0.5 leading-none">({podcast.appleRatingCount})</span>
                        </a>
                        ) : (
                            <div className="flex items-center gap-1 bg-amber-50 border border-amber-200 px-1.5 py-0 rounded text-amber-700 h-5">
                                <Star className="size-3 text-amber-500 fill-amber-500" />
                                <span className="font-semibold text-[10px] leading-none">{podcast.appleRating.toFixed(1)}</span>
                                <span className="text-[10px] text-amber-600/80 ml-0.5 leading-none">({podcast.appleRatingCount})</span>
                            </div>
                        )
                    ) : (
                        <div className="text-[10px] text-muted-foreground italic h-5 flex items-center px-1">No ratings</div>
                    )}

                    {/* Primary Market */}
                    {podcast.primaryMarket && (
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-normal gap-1 text-purple-600 bg-purple-50 border-purple-200">
                            {REGION_FLAGS[podcast.primaryMarket] || <MapPin className="size-3" />} {podcast.primaryMarket.toUpperCase()}
                        </Badge>
                    )}
                    
                    {/* Listener Estimate */}
                    {podcast.estimatedListeners != null && podcast.estimatedListeners > 0 && (
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-normal gap-1 text-indigo-600 bg-indigo-50 border-indigo-200">
                            <Users className="size-3" /> {formatListenerCount(podcast.estimatedListeners)}
                        </Badge>
                    )}

                    {/* YouTube Subscribers */}
                    {podcast.youtubeSubscriberCount && (
                        <a 
                            href={podcast.youtubeUrl || (podcast.youtubeHandle ? `https://youtube.com/${podcast.youtubeHandle}` : '#')}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-normal gap-1 text-red-600 bg-red-50 border-red-200 hover:bg-red-100 cursor-pointer transition-colors">
                                <Youtube className="size-3" /> {podcast.youtubeSubscriberCount}
                            </Badge>
                        </a>
                    )}

                    {/* Eps */}
                    {podcast.episodeCount && (
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-normal text-slate-500 border-slate-200">
                            {podcast.episodeCount} eps
                        </Badge>
                    )}
                    
                    {/* Last Episode Date */}
                    {podcast.lastEpisodeDate && (
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-normal gap-1 text-slate-500 border-slate-200">
                            <Calendar className="size-3" /> {dayjs(podcast.lastEpisodeDate).format('MMM D, YYYY')}
                        </Badge>
                    )}

                    {/* Email */}
                    {podcast.hasEmail && (
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-normal gap-1 text-emerald-600 bg-emerald-50 border-emerald-200">
                                <Mail className="size-3" />
                        </Badge>
                    )}
                 </div>
              </TableCell>
              <TableCell className="text-right align-middle py-3">
                 <div className="flex justify-end gap-2">
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={(e) => {
                            e.stopPropagation();
                            onAddToCampaign(podcast);
                        }}
                        title="Add to Campaign"
                    >
                        <ListPlus className="size-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 text-xs">
                        View
                    </Button>
                 </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
